import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    DataTable, columnTypes, CatcherWristbandHeatMap, LoadingSpinner,
    ratio, percent, pitchType as formatPitchType, images, slg, PITCH_TYPES, PITCH_SUB_TYPES, SmallSpinner,
    Checkbox, pitchTypeDisplayName, pitchSubType as formatPitchSubType,
} from 'yankees-react-utils';
import { PitcherRepertoire, Advance, PlayerReport, HeatMaps, useOktaQuery, Team, Stats } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import StatTooltip from '../../../components/StatTooltip';
import MatchupReportHitMon from './MatchupReportHitMon';
import useOpposingTeamSelector from '../../../utils/useOpposingTeamSelector';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import PlayerAuthoredReportNote from '../../../components/Notes/PlayerAuthoredReportNote';
import OpposingBatterMatchupNote from './OpposingBatterMatchupNote';
import MatchupReportCoachNote from './MatchupReportCoachNote';
import MatchupReportGenericHandednessHeatmaps from './MatchupReportGenericHandednessHeatmaps';
import { store } from '../../../store';
import VideoPlayerButton from '../../../components/VideoPlayerButton';
import BatterHeader from './BatterHeader';

const PAST_YEARS_TO_USE = 2; // i.e. 2 is the yearid "-2"

const getPitcherRepertoire = async (accessToken, pitcherId) => {
    const repertoire = await PitcherRepertoire.get(accessToken, pitcherId);
    return repertoire.filter(({ Visible }) => Visible).sort((a, b) => a.SortOrder - b.SortOrder);
};
const getMatchupHeatmaps = async (translate, accessToken, batterId, pitcherId, teamId, vsTeamId) => {
    const data = await Advance.overview(accessToken, { batterId, pitcherId, teamId, vsTeamId });
    const { heatmaps, overrides, pitchervelos, heatmapupdated, redgreenupdated, firstpitch, oz } = data;

    const earlyHeatmaps = heatmaps.filter((h) => h.playerid === batterId && h.situationsplit === 'Early');
    const lateHeatmaps = heatmaps.filter((h) => h.playerid === batterId && h.situationsplit === 'TwoStrikes');
    const earlyOverrides = overrides.filter((o) => o.BatterID === batterId && o.SituationSplit === 'Early');
    const lateOverrides = overrides.filter((o) => o.BatterID === batterId && o.SituationSplit === 'TwoStrikes');
    const pitches = PITCH_TYPES(translate).map((pt) => ({ id: 0, typeid: pt.id })).concat(PITCH_SUB_TYPES(translate)).reduce((acc, { typeid: pitchType, id: fxsubtype }) => {
        /* eslint-disable eqeqeq, object-property-newline */
        if (acc.earlyOverrides[pitchType] == null) acc.earlyOverrides[pitchType] = {};
        acc.earlyOverrides[pitchType][fxsubtype] = earlyOverrides.find((x) => x.PitchType == pitchType && x.FXSubtype == fxsubtype);
        // create a synthetic override record for display purposes
        if (!acc.earlyOverrides[pitchType][fxsubtype]) {
            acc.earlyOverrides[pitchType][fxsubtype] = {
                BatterID: batterId, BlankBox: null, GreenBox: null, PitchType: pitchType, FXSubType: fxsubtype, PitcherID: pitcherId, RedX: null,
                Selected: null, KFree: null, SituationSplit: 'Early', LastUpdateDate: null, LastUpdateUser: null,
            };
        }
        // if the model declares something should be an 0-0 pitch, and there aren't any manually set 0-0 pitches, set the synthetic record to true
        if (!earlyOverrides.some(({ Selected }) => Selected == true)) {
            const firstPitch = firstpitch.find((x) => x.batterid == batterId && x.type == pitchType && x.fxsubtype == fxsubtype);
            if (firstPitch && acc.earlyOverrides[pitchType][fxsubtype].Selected == null) {
                acc.earlyOverrides[pitchType][fxsubtype].Selected = true;
                acc.earlyOverrides[pitchType][fxsubtype].KFree = !!firstPitch.kfree;
            }
        }
        const earlycode = earlyHeatmaps.find((x) => x.x == 0 && x.y == 0 && x.pitchtype == pitchType && x.fxsubtype == fxsubtype);
        if (earlycode) {
            if (acc.earlyOverrides[pitchType][fxsubtype].val == null) {
                acc.earlyOverrides[pitchType][fxsubtype].val = earlycode.val;
            } else {
                acc.earlyOverrides[pitchType][fxsubtype].valoverride = true;
            }
        }

        if (acc.lateOverrides[pitchType] == null) acc.lateOverrides[pitchType] = {};
        acc.lateOverrides[pitchType][fxsubtype] = lateOverrides.find((x) => x.PitchType == pitchType && x.FXSubtype == fxsubtype);
        // create a synthetic override record for display purposes
        if (acc.lateOverrides[pitchType][fxsubtype] == null) {
            acc.lateOverrides[pitchType][fxsubtype] = {
                BatterID: batterId, BlankBox: null, GreenBox: null, PitchType: pitchType, PitcherID: pitcherId, FXSubType: fxsubtype, RedX: null,
                Selected: null, OTZ: null, SituationSplit: 'TwoStrikes', LastUpdateDate: null, LastUpdateUser: null,
            };
        }
        // if the model declares something should be an OTZ pitch, and there aren't any manually set OTZ pitches, set the synthetic record to true
        if (!lateOverrides.some(({ OTZ }) => OTZ == true)) {
            const otz = oz.find((x) => x.batterid == batterId && x.type == pitchType && x.fxsubtype == fxsubtype);
            if (otz && acc.lateOverrides[pitchType][fxsubtype].OTZ == null) {
                acc.lateOverrides[pitchType][fxsubtype].OTZ = otz.oz;
            }
        }
        const latecode = lateHeatmaps.find((x) => x.x == 0 && x.y == 0 && x.pitchtype == pitchType && x.fxsubtype == fxsubtype);
        if (latecode) {
            if (acc.lateOverrides[pitchType][fxsubtype].val == null) {
                acc.lateOverrides[pitchType][fxsubtype].val = latecode.val;
            } else {
                acc.lateOverrides[pitchType][fxsubtype].valoverride = true;
            }
        }
        if (acc.earlyHeatmaps[pitchType] == null) acc.earlyHeatmaps[pitchType] = [];
        if (acc.lateHeatmaps[pitchType] == null) acc.lateHeatmaps[pitchType] = [];
        acc.earlyHeatmaps[pitchType][fxsubtype] = earlyHeatmaps.filter((x) => x.pitchtype == pitchType && x.fxsubtype == fxsubtype);
        acc.lateHeatmaps[pitchType][fxsubtype] = lateHeatmaps.filter((x) => x.pitchtype == pitchType && x.fxsubtype == fxsubtype);
        /* eslint-enable eqeqeq, object-property-newline */
        return acc;
    }, { earlyOverrides: {}, lateOverrides: {}, earlyHeatmaps: {}, lateHeatmaps: {} });
    return {
        ...pitches,
        heatmaps,
        heatmapUpdated: heatmapupdated && heatmapupdated[0],
        redgreenUpdated: redgreenupdated && redgreenupdated[0],
        showFb94: pitchervelos.some(({ type, velo }) => type === 1 && velo > 94),
    };
};
const pitchCountGroups = (translate) => [
    { name: '0-0', isInGroup: (b, s) => b === 0 && s === 0, videoParams: { balls: 0, strikes: 0 } },
    { name: translate('sections.opponent.player.early'), isInGroup: (_b, s) => s < 2, videoParams: { earlycount: true } },
    { name: translate('sections.opponent.player.hitter-ahead'), isInGroup: (b, s) => s < 2 && b > s, videoParams: { hitterahead: true } },
    { name: `2-${translate('baseball.strikes_one')}`, isInGroup: (_b, s) => s === 2, videoParams: { strikes: 2 } },
    { name: '3-2', isInGroup: (b, s) => b === 3 && s === 2, videoParams: { balls: 3, strikes: 2 } },
];
const swingStatClass = (translate, pitchCountGroupName, percentage, total) => {
    let color = null;

    const swingStatClassBuckets = {
        '0-0': { darkblue: 0.14, lightblue: 0.21, lightred: 0.35, darkred: 0.42 },
        [`${translate('sections.opponent.player.early')}`]: { darkblue: 0.30, lightblue: 0.35, lightred: 0.46, darkred: 0.51 },
        [`${translate('sections.opponent.player.hitter-ahead')}`]: { darkblue: 0.40, lightblue: 0.45, lightred: 0.56, darkred: 0.62 },
        [`2-${translate('baseball.strikes_one')}`]: { darkblue: 0.51, lightblue: 0.56, lightred: 0.66, darkred: 0.71 },
        '3-2': { darkblue: 0.63, lightblue: 0.69, lightred: 0.79, darkred: 0.85 },
    };

    if (total > 20) {
        const bucket = swingStatClassBuckets[pitchCountGroupName];
        if (percentage < bucket.darkblue) color = 'darkblue';
        else if (percentage < bucket.lightblue) color = 'lightblue';
        else if (percentage > bucket.darkred) color = 'darkred';
        else if (percentage > bucket.lightred) color = 'lightred';
    }
    return color;
};
const resultBuckets = [
    /* eslint-disable max-len */
    { islhb: false, islhp: false, pitchtype: null, swingavg: 0.489365597909633, swingstd: 0.0846443188275069, chaseavg: 0.326317339953889, chasestd: 0.0992797903967828, strikeavg: 0.632148245845538, strikestd: 0.0609313232510306, missavg: 0.274872961573618, missstd: 0.11636064575894, slgavg: 0.423058896149959, slgstd: 0.169360880498928, gbavg: 0.448348914595663, gbstd: 0.143607395316651, veloavg: 87.455693, velostd: 4.49687656628812 },
    { islhb: true, islhp: true, pitchtype: null, swingavg: 0.481617913980869, swingstd: 0.086778100929764, chaseavg: 0.310208158857433, chasestd: 0.103582304636927, strikeavg: 0.631204876490121, strikestd: 0.0654766406912074, missavg: 0.25542543915712, missstd: 0.117536659747013, slgavg: 0.401557589126228, slgstd: 0.211981621090912, gbavg: 0.461483231381789, gbstd: 0.159496099896425, veloavg: 86.207343, velostd: 4.98029806364512 },
    { islhb: true, islhp: false, pitchtype: null, swingavg: 0.476083577707151, swingstd: 0.0787434606420287, chaseavg: 0.312654251718952, chasestd: 0.104476095517006, strikeavg: 0.620651537521689, strikestd: 0.0571278728821315, missavg: 0.251789251717169, missstd: 0.103131846047836, slgavg: 0.432600753539568, slgstd: 0.160367341658256, gbavg: 0.43977480150514, gbstd: 0.124310044593754, veloavg: 87.628943, velostd: 4.28694456725231 },
    { islhb: false, islhp: true, pitchtype: null, swingavg: 0.465786454855847, swingstd: 0.087980426760725, chaseavg: 0.300979379076365, chasestd: 0.111030704107995, strikeavg: 0.623394487054691, strikestd: 0.0650404435157748, missavg: 0.242918675352744, missstd: 0.11425956487508, slgavg: 0.459999188848521, slgstd: 0.215399087055865, gbavg: 0.43204010819429, gbstd: 0.149497055864409, veloavg: 88.503342, velostd: 4.63165956200012 },
    { islhb: false, islhp: true, pitchtype: 10, swingavg: 0.467435082386812, swingstd: 0.0598892734940396, chaseavg: 0.254173887918615, chasestd: 0.0785159952036594, strikeavg: 0.64124272894332, strikestd: 0.0432238304352771, missavg: 0.185339152265192, missstd: 0.0768698820214215, slgavg: 0.514802486558364, slgstd: 0.141697221330043, gbavg: 0.316955974917483, gbstd: 0.0915961877060109, veloavg: 90.178100, velostd: 3.20558616938213 },
    { islhb: true, islhp: true, pitchtype: 9, swingavg: 0.496057890612307, swingstd: 0.0639228273456091, chaseavg: 0.316094032807348, chasestd: 0.0913259722072381, strikeavg: 0.670869202263509, strikestd: 0.0446611816199163, missavg: 0.139545252009031, missstd: 0.0584229884122902, slgavg: 0.387016531410618, slgstd: 0.119552257742061, gbavg: 0.602975679808473, gbstd: 0.10447444871296, veloavg: 85.492403, velostd: 3.55433165456522 },
    { islhb: false, islhp: false, pitchtype: 10, swingavg: 0.477496035131691, swingstd: 0.05066390654619, chaseavg: 0.267408267933348, chasestd: 0.0713475754760229, strikeavg: 0.661631359700791, strikestd: 0.031670995066936, missavg: 0.205639881196879, missstd: 0.0708778472031078, slgavg: 0.502749765213554, slgstd: 0.0866232937705245, gbavg: 0.330072156769577, gbstd: 0.0726505016942312, veloavg: 91.662439, velostd: 2.5387400177152 },
    { islhb: true, islhp: false, pitchtype: 4, swingavg: 0.501883155810744, swingstd: 0.0625488862684677, chaseavg: 0.343462367305572, chasestd: 0.0779727098177417, strikeavg: 0.604739927102126, strikestd: 0.0500347280017598, missavg: 0.283738512322617, missstd: 0.0769897018033413, slgavg: 0.4069809329835, slgstd: 0.099031341962035, gbavg: 0.500133918681822, gbstd: 0.0685689704054199, veloavg: 85.069533, velostd: 3.08170751748402 },
    { islhb: true, islhp: true, pitchtype: 4, swingavg: 0.558667972119279, swingstd: 0.0854700895846735, chaseavg: 0.38896631496667, chasestd: 0.116296289698461, strikeavg: 0.647036222208393, strikestd: 0.0746185114796334, missavg: 0.281593603029985, missstd: 0.117161415390988, slgavg: 0.449358096748435, slgstd: 0.345554261738894, gbavg: 0.478456840805977, gbstd: 0.179619924477876, veloavg: 84.912319, velostd: 7.30911142890217 },
    { islhb: true, islhp: false, pitchtype: 8, swingavg: 0.513364472072067, swingstd: 0.0710191023709979, chaseavg: 0.331478311860328, chasestd: 0.0931953363919572, strikeavg: 0.647228060567263, strikestd: 0.0537968113475408, missavg: 0.21699151323863, missstd: 0.0683280931119078, slgavg: 0.462580915878146, slgstd: 0.190139684346702, gbavg: 0.40130219100287, gbstd: 0.100203226434052, veloavg: 87.244035, velostd: 3.40423760706166 },
    { islhb: true, islhp: false, pitchtype: 9, swingavg: 0.439127950753534, swingstd: 0.0522079679312547, chaseavg: 0.208853110100926, chasestd: 0.0530255068635295, strikeavg: 0.622446990336297, strikestd: 0.031175847665822, missavg: 0.136774099804238, missstd: 0.0489188663702029, slgavg: 0.533176728038005, slgstd: 0.125854354543653, gbavg: 0.445911551306255, gbstd: 0.0848135948239764, veloavg: 91.367185, velostd: 2.67432473896148 },
    { islhb: true, islhp: true, pitchtype: 10, swingavg: 0.459075358046367, swingstd: 0.049166870205472, chaseavg: 0.237075105128711, chasestd: 0.0663883457911902, strikeavg: 0.654677151688238, strikestd: 0.0360584455326986, missavg: 0.209754515126439, missstd: 0.0631357358808241, slgavg: 0.471768358136229, slgstd: 0.13839249148553, gbavg: 0.360935090261863, gbstd: 0.110102541290293, veloavg: 90.704038, velostd: 2.99372332791039 },
    { islhb: false, islhp: true, pitchtype: 7, swingavg: 0.476768345547077, swingstd: 0.172230242222392, chaseavg: 0.340423717696445, chasestd: 0.208669386869404, strikeavg: 0.596742039784305, strikestd: 0.152848390003837, missavg: 0.324463793781976, missstd: 0.208707938822666, slgavg: 0.463806900170536, slgstd: 0.546389868589833, gbavg: 0.527685950413223, gbstd: 0.35778641087502, veloavg: 92.751184, velostd: 8.61800241388698 },
    { islhb: false, islhp: false, pitchtype: 6, swingavg: 0.425357319311124, swingstd: 0.160287205401634, chaseavg: 0.250347222222222, chasestd: 0.146568912077747, strikeavg: 0.587849672971956, strikestd: 0.105165886770074, missavg: 0.250457875457875, missstd: 0.206336497946431, slgavg: 0.508333333333333, slgstd: 0.581937104548931, gbavg: 0.464583333333333, gbstd: 0.410616793121824, veloavg: 92.466510, velostd: 8.83881933223083 },
    { islhb: false, islhp: true, pitchtype: 2, swingavg: 0.404584115595355, swingstd: 0.0939451000627003, chaseavg: 0.304120839934498, chasestd: 0.104077106402235, strikeavg: 0.598342532041661, strikestd: 0.0655954618873022, missavg: 0.283187297946731, missstd: 0.104356666839528, slgavg: 0.440344662569246, slgstd: 0.208839940892644, gbavg: 0.475728789309495, gbstd: 0.133499768979972, veloavg: 88.034753, velostd: 4.26811736751462 },
    { islhb: false, islhp: false, pitchtype: 2, swingavg: 0.420099069262312, swingstd: 0.0766966944499252, chaseavg: 0.312304677688593, chasestd: 0.0834932466055259, strikeavg: 0.59881101075569, strikestd: 0.0474519568913195, missavg: 0.304153626253585, missstd: 0.0897624174647903, slgavg: 0.417681469864599, slgstd: 0.143480158957506, gbavg: 0.442731773196131, gbstd: 0.0947970021291048, veloavg: 86.318190, velostd: 3.2339893257258 },
    { islhb: true, islhp: false, pitchtype: 10, swingavg: 0.469497807654063, swingstd: 0.0474157512421289, chaseavg: 0.246087577628219, chasestd: 0.0598364774521234, strikeavg: 0.637142288107273, strikestd: 0.0284753848461769, missavg: 0.1911283007541, missstd: 0.059581049904207, slgavg: 0.491304119386463, slgstd: 0.088750071316526, gbavg: 0.321288410299276, gbstd: 0.0791151010292111, veloavg: 89.544063, velostd: 3.0303496729717 },
    { islhb: true, islhp: true, pitchtype: 7, swingavg: 0.514924242424242, swingstd: 0.0386322784530716, chaseavg: 0.425, chasestd: 0.084254171600573, strikeavg: 0.596709956709957, strikestd: 0.0879767489740223, missavg: 0.29047619047619, missstd: 0.246517697248472, slgavg: 0.316666666666667, slgstd: 0.541346058963724, gbavg: 0.733333333333333, gbstd: 0.252762514801719, veloavg: 80.820906, velostd: 4.46339109901303 },
    { islhb: false, islhp: true, pitchtype: 8, swingavg: 0.503267837113221, swingstd: 0.0714818086038236, chaseavg: 0.315190870507095, chasestd: 0.100393527898777, strikeavg: 0.647226751343763, strikestd: 0.0647142196384164, missavg: 0.204318436890499, missstd: 0.078732801458171, slgavg: 0.438133885430402, slgstd: 0.228986297942972, gbavg: 0.428919321120196, gbstd: 0.152523033764191, veloavg: 87.385245, velostd: 4.52041523243551 },
    { islhb: false, islhp: false, pitchtype: 8, swingavg: 0.483782419155575, swingstd: 0.0624188846151467, chaseavg: 0.287047232410401, chasestd: 0.0747197719176852, strikeavg: 0.635342991188636, strikestd: 0.0474379314195941, missavg: 0.252151993667713, missstd: 0.0712082799874987, slgavg: 0.440500243592726, slgstd: 0.160696422167188, gbavg: 0.419786504443622, gbstd: 0.113894619950625, veloavg: 87.489508, velostd: 3.64249935536111 },
    { islhb: false, islhp: true, pitchtype: 4, swingavg: 0.501677611909889, swingstd: 0.0701605291602694, chaseavg: 0.345094137755237, chasestd: 0.0845656142403831, strikeavg: 0.610615569191315, strikestd: 0.05255382324518, missavg: 0.288908242305823, missstd: 0.0845449274516953, slgavg: 0.426038576663731, slgstd: 0.14712145499092, gbavg: 0.477272057177075, gbstd: 0.0936068646638148, veloavg: 85.080825, velostd: 3.6633262517719 },
    { islhb: false, islhp: false, pitchtype: 7, swingavg: 0.556262402735454, swingstd: 0.108375467201688, chaseavg: 0.406462517983338, chasestd: 0.128291857933822, strikeavg: 0.616742871627865, strikestd: 0.101908828107376, missavg: 0.361128318248964, missstd: 0.134427406333662, slgavg: 0.30814604541987, slgstd: 0.240115319759646, gbavg: 0.475650772170838, gbstd: 0.225054343640942, veloavg: 86.182444, velostd: 7.09395108349247 },
    { islhb: false, islhp: true, pitchtype: 3, swingavg: 0.485903475506902, swingstd: 0.082798609252657, chaseavg: 0.36549926484123, chasestd: 0.104469957463304, strikeavg: 0.623892901614383, strikestd: 0.0628064779836174, missavg: 0.332041194085803, missstd: 0.098016191291707, slgavg: 0.402446998060121, slgstd: 0.182142995575174, gbavg: 0.391298851981014, gbstd: 0.125381842770865, veloavg: 87.828738, velostd: 3.9521581169993 },
    { islhb: false, islhp: false, pitchtype: 3, swingavg: 0.480186520124306, swingstd: 0.067647584009263, chaseavg: 0.345739326462197, chasestd: 0.0783567731073771, strikeavg: 0.628624201091542, strikestd: 0.0417600919349885, missavg: 0.355712593606916, missstd: 0.0725853648549168, slgavg: 0.385824264132234, slgstd: 0.09445040559841, gbavg: 0.434139156812411, gbstd: 0.0740371689985652, veloavg: 85.633099, velostd: 2.7562434963192 },
    { islhb: true, islhp: true, pitchtype: 2, swingavg: 0.40888481863219, swingstd: 0.0905196399484413, chaseavg: 0.276797274846451, chasestd: 0.0937187720210773, strikeavg: 0.576309183575465, strikestd: 0.0639393352973978, missavg: 0.263585525546368, missstd: 0.104716396444195, slgavg: 0.392389061371395, slgstd: 0.192545867408859, gbavg: 0.443252698130119, gbstd: 0.138028078841172, veloavg: 85.890851, velostd: 4.24477116809913 },
    { islhb: true, islhp: false, pitchtype: 6, swingavg: 0.448205949046285, swingstd: 0.126180357049874, chaseavg: 0.251984126984127, chasestd: 0.224872587871475, strikeavg: 0.63577430972389, strikestd: 0.140694898070016, missavg: 0.128571428571429, missstd: 0.167971085947212, slgavg: 0.0857142857142857, slgstd: 0.157359158493889, gbavg: 0.523809523809524, gbstd: 0.447922895859232, veloavg: 77.832835, velostd: 13.4876542699531 },
    { islhb: true, islhp: true, pitchtype: 8, swingavg: 0.489015587473381, swingstd: 0.0825214071815195, chaseavg: 0.285705551505162, chasestd: 0.086472722322163, strikeavg: 0.622824608333684, strikestd: 0.0686268013600822, missavg: 0.258145998036838, missstd: 0.0924357518390995, slgavg: 0.392378740728637, slgstd: 0.190098258400402, gbavg: 0.429955667101632, gbstd: 0.171992065670952, veloavg: 86.334065, velostd: 4.23709045758749 },
    { islhb: false, islhp: true, pitchtype: 9, swingavg: 0.429166144453935, swingstd: 0.063059285730884, chaseavg: 0.212155324061051, chasestd: 0.0708292142812917, strikeavg: 0.625561481859795, strikestd: 0.0454600787458371, missavg: 0.14378447745083, missstd: 0.0610906109977772, slgavg: 0.537297749930548, slgstd: 0.186755928504752, gbavg: 0.478685559895847, gbstd: 0.111587559291308, veloavg: 91.536964, velostd: 3.14183787497419 },
    { islhb: false, islhp: false, pitchtype: 9, swingavg: 0.47209275758879, swingstd: 0.0520131063015819, chaseavg: 0.298730660845201, chasestd: 0.0732487049863736, strikeavg: 0.661692504071312, strikestd: 0.0386792895906559, missavg: 0.134315195420561, missstd: 0.0489470801488807, slgavg: 0.47246205915891, slgstd: 0.107937388233448, gbavg: 0.556353671165884, gbstd: 0.0929779644367284, veloavg: 88.363061, velostd: 2.87461270556943 },
    { islhb: true, islhp: false, pitchtype: 2, swingavg: 0.40715928254988, swingstd: 0.0836698593186352, chaseavg: 0.31892796860649, chasestd: 0.0927702053584294, strikeavg: 0.598929552009121, strikestd: 0.0525733427131792, missavg: 0.307308345602268, missstd: 0.087326222188799, slgavg: 0.399902943991728, slgstd: 0.147016977244613, gbavg: 0.458263056704854, gbstd: 0.0969508937570062, veloavg: 87.349568, velostd: 3.1228429855577 },
    { islhb: true, islhp: false, pitchtype: 3, swingavg: 0.491334904615879, swingstd: 0.0733060838869174, chaseavg: 0.363627844799588, chasestd: 0.0908188329401272, strikeavg: 0.632221249218716, strikestd: 0.052365594680015, missavg: 0.310351426559548, missstd: 0.0799791670613392, slgavg: 0.404697030403518, slgstd: 0.111571721370225, gbavg: 0.391094944362375, gbstd: 0.0762970767216662, veloavg: 87.955755, velostd: 3.28092706227935 },
    { islhb: false, islhp: false, pitchtype: 4, swingavg: 0.540425647058912, swingstd: 0.0718942151392312, chaseavg: 0.372208705156515, chasestd: 0.0884176417635053, strikeavg: 0.625504825314587, strikestd: 0.0572922023306194, missavg: 0.312834549620019, missstd: 0.08996761984058, slgavg: 0.427672766812987, slgstd: 0.159863390041257, gbavg: 0.47849457929434, gbstd: 0.123666242665484, veloavg: 86.142670, velostd: 3.83150804221982 },
    { islhb: true, islhp: true, pitchtype: 3, swingavg: 0.475443831736815, swingstd: 0.0717124850104914, chaseavg: 0.347780532263902, chasestd: 0.0842702232335703, strikeavg: 0.618166346239138, strikestd: 0.0493875048903964, missavg: 0.377231529554129, missstd: 0.0944559474207154, slgavg: 0.322964817320484, slgstd: 0.133075648045853, gbavg: 0.432411865878704, gbstd: 0.0990430858521257, veloavg: 84.324725, velostd: 3.42313477777468 },
    { islhb: true, islhp: false, pitchtype: 7, swingavg: 0.512315786629656, swingstd: 0.0837339312026654, chaseavg: 0.380709150259966, chasestd: 0.111007753417189, strikeavg: 0.60071442213452, strikestd: 0.0839600804453676, missavg: 0.325507023545451, missstd: 0.104763111582182, slgavg: 0.355672338233003, slgstd: 0.214517217763513, gbavg: 0.55410434402658, gbstd: 0.133990835087643, veloavg: 85.609802, velostd: 5.02249258408066 },
    { islhb: false, islhp: false, pitchtype: 1, swingavg: 0.505272422339929, swingstd: 0.0508704271240371, chasesavg: 0.304465484491346, chasestd: 0.0720391007513183, strikesavg: 0.669361805383635, strikesstd: 0.0335764448522967, missesavg: 0.219963924815651, missesstd: 0.0748905999515111, slgavg: 0.435517676253182, slgstd: 0.097229742652127, gbavg: 0.436507825881818, gbstd: 0.0912054660081951, veloavg: 90.082923, velostd: 2.73813753800905 },
    { islhb: false, islhp: true, pitchtype: 1, swingavg: 0.514460163173932, swingstd: 0.0792077456939414, chasesavg: 0.292871366839648, chasestd: 0.106312252299545, strikesavg: 0.658211933642576, strikesstd: 0.0613656829141268, missesavg: 0.228053723528835, missesstd: 0.109007896996347, slgavg: 0.396732116855199, slgstd: 0.209002415132846, gbavg: 0.4408554181144, gbstd: 0.180704625483145, veloavg: 89.857070, velostd: 5.18444363243366 },
    { islhb: true, islhp: false, pitchtype: 1, swingavg: 0.502085555832432, swingstd: 0.0512708959665368, chasesavg: 0.270781007159105, chasestd: 0.0638865541460735, strikesavg: 0.648605568872694, strikesstd: 0.0336314455394019, missesavg: 0.203753855689852, missesstd: 0.0689896028717779, slgavg: 0.447295746761426, slgstd: 0.110209817372206, gbavg: 0.389065266776263, gbstd: 0.0970682484602315, veloavg: 89.570407, velostd: 3.02330707478627 },
    { islhb: true, islhp: true, pitchtype: 1, swingavg: 0.509002437169423, swingstd: 0.0710652692555412, chasesavg: 0.297550205122401, chasestd: 0.100750924256597, strikesavg: 0.666320631804583, strikesstd: 0.0568517544275849, missesavg: 0.243071658184194, missesstd: 0.093572710846902, slgavg: 0.379576966865606, slgstd: 0.222088979714016, gbavg: 0.497657918794318, gbstd: 0.179097516595399, veloavg: 88.415373, velostd: 4.71367140036088 },
    /* eslint-enable max-len */
];
const resultStatClass = (stat, pitchtype, number, islhb, islhp) => {
    const bucket = resultBuckets.find((b) => b.islhb === islhb && b.islhp === islhp && b.pitchtype === pitchtype);
    if (!bucket) return null;

    const avg = bucket[`${stat}avg`];
    const std = bucket[`${stat}std`];
    if (number < (avg - (2 * std))) return 'darkblue';
    if (number < (avg - std)) return 'lightblue';
    if (number > (avg + (2 * std))) return 'darkred';
    if (number > (avg + std)) return 'lightred';
    return '';
};
const calculateStats = (playerStats, islhb, islhp, pitchtype = null, pitchsubtype = null, fxsubtype = 0) => {
    const stats = {
        pitchtype,
        pitchsubtype: pitchsubtype || playerStats.pitchsubtype || null,
        fxsubtype,
        pf: playerStats.pf,
        hitvelo: { velo: null },
        sw: { percentage: null },
        strikes: { percentage: null },
        ch: { percentage: null },
        miss: { percentage: null },
        gb: { percentage: null },
        slg: { decimal: null },
    };
    if (playerStats.pf > 10) {
        stats.grade = playerStats.grade;

        stats.hitvelo.velo = playerStats.hitvelo || null;
        stats.hitvelo.class = resultStatClass('velo', pitchtype, stats.hitvelo.velo, islhb, islhp);

        stats.sw.percentage = ratio(playerStats.sw, playerStats.pf);
        stats.sw.class = resultStatClass('swing', pitchtype, stats.sw.percentage, islhb, islhp);

        stats.strikes.percentage = ratio(playerStats.strikes, playerStats.pf);
        stats.strikes.class = resultStatClass('strike', pitchtype, stats.strikes.percentage, islhb, islhp);

        stats.ch.percentage = ratio(playerStats.ch, playerStats.outz);
        stats.ch.class = resultStatClass('chase', pitchtype, stats.ch.percentage, islhb, islhp);

        stats.miss.percentage = ratio(playerStats.sw - playerStats.cn, playerStats.sw);
        stats.miss.class = resultStatClass('miss', pitchtype, stats.miss.percentage, islhb, islhp);

        stats.gb.percentage = ratio(playerStats.gb, playerStats.gb + playerStats.fb + playerStats.ld + playerStats.pop);
        stats.gb.class = resultStatClass('gb', pitchtype, stats.gb.percentage, islhb, islhp);

        stats.slg.decimal = slg(playerStats.b1, playerStats.b2, playerStats.b3, playerStats.hr, playerStats.ab);
        stats.slg.class = resultStatClass('slg', pitchtype, stats.slg.decimal, islhb, islhp);
    }
    return stats;
};
const mapVideoParamsToPitchTypeResults = (pitchTypeResults, batterid, islhp) => {
    if (!pitchTypeResults) return;
    pitchTypeResults.forEach((ptr) => {
        const { pitchtype, fxsubtype } = ptr;
        const defaultParams = { batterid, islhp, yearid: -2, fxpitchtype: pitchtype, fxsubtype };
        ptr.sw.videoParams = { isswing: true, ...defaultParams };
        ptr.strikes.videoParams = { isstrike: true, ...defaultParams };
        ptr.ch.videoParams = { ischase: true, ...defaultParams };
        ptr.miss.videoParams = { ismiss: true, ...defaultParams };
    });
};
const mapVideoParamsToSwingRates = (translate, swingRates, batterid, islhp) => {
    if (!swingRates) return;
    swingRates.forEach((sr) => {
        pitchCountGroups(translate).forEach(({ name, videoParams }) => {
            if (sr[name] && videoParams) sr[name].videoParams = { batterid, islhp, yearid: -2, fxpitchtype: sr.pitchType, fxsubtype: sr.subType, ...videoParams };
        });
    });
};
const getAdvanceOverview = async (translate, accessToken, queryParams, islhb, islhp) => {
    const qParams = { ...queryParams, startYear: queryParams.startYear || queryParams.endYear, endYear: queryParams.endYear || queryParams.startYear, version: 2 };
    const data = await PlayerReport.adov(accessToken, qParams);
    const swingRateData = data.swingsbycount.reduce((acc1, s) => {
        if (!s.pitchtype) return acc1;
        const { pitchtype } = s;
        const fxsubtype = s.fxsubtype || 0;
        if (!acc1[pitchtype]) acc1[pitchtype] = {};
        if (!acc1[pitchtype][fxsubtype]) acc1[pitchtype][fxsubtype] = {};
        acc1[pitchtype][fxsubtype] = pitchCountGroups(translate).reduce((acc2, { name, isInGroup }) => {
            if (!acc2[name]) acc2[name] = { total: 0, swings: 0 };
            if (isInGroup(s.balls, s.strikes)) {
                const total = acc2[name].total + s.num;
                const swings = acc2[name].swings + s.swings;
                const percentage = swings / total;

                acc2[name] = { total, swings, percentage, class: swingStatClass(translate, name, percentage, total) };
            }
            return acc2;
        }, acc1[pitchtype][fxsubtype]);

        return acc1;
    }, {});

    const overallSwings = pitchCountGroups(translate).reduce((acc1, { name }) => ({
        ...acc1,
        [name]: Object.values(swingRateData)
            .flatMap((ptBucket) => Object.values(ptBucket))
            .map((subGroup) => subGroup[name])
            .reduce((acc2, g) => {
                const total = acc2.total + g.total;
                const swings = acc2.swings + g.swings;
                const percentage = swings / total;
                return {
                    total,
                    swings,
                    percentage,
                    class: swingStatClass(translate, name, percentage, total),
                };
            }, { total: 0, swings: 0 }),
    }), {});
    const overallResults = calculateStats(data.overallstats[0], islhb, islhp);
    const pitchTypeResults = data.pitchtypestats.reduce((acc, pts) => {
        const { pitchtype } = pts;
        const fxsubtype = pts.fxsubtype ?? 0;
        if (!acc[pitchtype]) acc[pitchtype] = {};
        acc[pitchtype][fxsubtype] = calculateStats(pts, islhb, islhp, pitchtype, pts.pitchsubtype, fxsubtype);
        return acc;
    }, {});
    let normalizepa = 600;
    let swrisphilite = false;
    let sw1prisphilite = false;
    if (data.stats?.length) {
        normalizepa = data.stats[data.stats.length - 1].pa;
        swrisphilite = data.stats.some((s) => ratio(s.swrisp, s.pitrisp) > 0.5 + ratio(s.sw, s.pf - s.iballs - s.pitchouts));
        sw1prisphilite = data.stats.some((s) => ratio(s.sw1pitrisp, s.pit1risp) > 0.08 + ratio(s.sw1stpit, s.pf00count));
    }

    return {
        ...data,
        pitchTypeResults: Object.values(pitchTypeResults).sort((a, b) => a.pitchsubtype - b.pitchsubtype),
        overallResults,
        swingRateData,
        overallSwings,
        normalizepa,
        swrisphilite,
        sw1prisphilite,
    };
};

const StyledPitchGroupBox = styled.div`
    display: flex;
    justify-content: center;
    background-repeat: no-repeat;
    background-size: 150px;
    background-size: contain;
    padding: 0 50px;
    ${({ $islhb }) => ($islhb ? `
        background-position: 0 0;
        background-image: url(${images.lhbImgFront});
    ` : `
        background-position: right 0;
        background-image: url(${images.rhbImgFront});
    `)}
    p {
        font-size: 1.5em;
        font-weight: bold;
        text-align: center;
        background: #C7C7C7 !important;
        -webkit-print-color-adjust: exact; 
        padding: 5px;
        border-bottom: 2px solid #000;
        margin: 0;
    }
`;
const StyledMatchupReport = styled.div`
    .table {
        table-layout: fixed;
        td small {
            margin-left: 5px;
        }
        text-align: center !important;
    }
    .lightblue {
        background-color: #AACCFF !important;
        -webkit-print-color-adjust: exact;
    }
    .darkblue {
        background-color: #66AAFF !important;
        -webkit-print-color-adjust: exact;
    }
    .lightred {
        background-color: #F2B9B4 !important;
        -webkit-print-color-adjust: exact;
    }
    .darkred {
        background-color: #E9877F !important;
        -webkit-print-color-adjust: exact;
    }
    .background-grade0 {
        background-color: #013220 !important;
        color: #fff;
    }
    .background-grade1 {
        background-color: #335B4C !important;
        color: #fff;
    }
    .background-grade2 {
        background-color: #668479 !important;
        color: #fff;
    }
    .background-grade3 {
        background-color: #99ADA5 !important;
    }
    .background-grade4 {
        background-color: #CCD6D2 !important;
    }
    .background-grade5 {
        background-color: #000 !important;
        color: #fff;
    }
    .background-grade6 {
        background-color: #E7CCCC !important;
    }
    .background-grade7 {
        background-color: #D09999 !important;
    }
    .background-grade8 {
        background-color: #B96666 !important;
        color: #fff;
    }
    .background-grade9 {
        background-color: #A23333 !important;
        color: #fff;
    }
    .background-grade10 {
        background-color: #8B0000 !important;
        color: #fff;
    }
`;
const StyledReportCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    word-wrap: break-word;
    background-color: #fff;
    background-clip: border-box;
    border: 1px solid #bcc8d1;
    margin-top: 10px;
    border-radius: 0.25rem;
    
    .card-body-styled {
        min-height: 1px;
        padding: 1.25rem;
    }

    .card-body-border {
        border: 1px solid #bcc8d1;
        display: flex;
    }

    div.card {
        border: 0;
    }

    div.card-body {
        padding: 0.75rem;
    }
`;
const { TEXT, NODE, PERCENT, RATE } = columnTypes;
const swingRateTableColumns = (translate) => [
    { id: 'FXType', title: translate('data.pitch-type'), type: TEXT, transform: (row) => `${formatPitchType(row.pitchType, true, 'Overall', translate)}${row.subType ? ` (${formatPitchSubType(row.subType, true, undefined, translate)}) ` : ''}` },
    ...pitchCountGroups(translate).map(({ name }) => ({
        id: `${name}.percentage`,
        title: name,
        type: NODE,
        cellClass: (row) => row[name]?.class,
        transform: (row) => {
            if (!row[name]) return (<span />);
            if (row[name]?.total === 0) { return <span>--</span>; }
            const valueDisplay = (
                <>
                    {(row[name].total >= 10 || !!row.pitchType) && (<span>{percent(row[name].percentage)}</span>)}
                    {(row[name].total < 10 && !row.pitchType) && (<span>--</span>)}
                </>
            );
            return (
                <span>
                    {row[name]?.total && row[name]?.videoParams && <VideoPlayerButton text={valueDisplay} textColor="#00008B" showCameraIcon={false} {...row[name]?.videoParams} />}
                    {row[name]?.total && !row[name]?.videoParams && valueDisplay }
                </span>
            );
        },
    })),
];
const pitchTypeResultsColumns = (translate) => [
    {
        id: 'pitchtype',
        title: translate('data.pitch-type'),
        type: TEXT,
        transform: (row) => `${formatPitchType(row.pitchtype, true, 'Overall', translate)}${row.fxsubtype ? ` (${formatPitchSubType(row.fxsubtype, true, undefined, translate)}) ` : ''}${row.pitchsubtype === 94 ? ' > 94' : ''}`,
    },
    {
        id: 'sw.percentage',
        title: () => (<StatTooltip statistic="Sw">{translate('sections.opponent.player.swing-pct')}</StatTooltip>),
        type: NODE,
        cellClass: ({ sw }) => sw?.class,
        transform: ({ sw, pf }) => {
            if (!sw) return (<span />);
            if (pf < 10) { return <span>--</span>; }
            const valueDisplay = percent(sw.percentage);
            return (
                <span>
                    {sw.videoParams && <VideoPlayerButton text={valueDisplay} textColor="#00008B" showCameraIcon={false} {...sw.videoParams} />}
                    {!sw.videoParams && valueDisplay }
                </span>
            );
        },
    },
    {
        id: 'ch.percentage',
        title: () => (<StatTooltip statistic="Ch">{translate('sections.opponent.player.chase-pct')}</StatTooltip>),
        type: NODE,
        cellClass: ({ ch }) => ch?.class,
        transform: ({ ch, pf }) => {
            if (!ch) return (<span />);
            if (pf < 10) { return <span>--</span>; }
            const valueDisplay = percent(ch.percentage);
            return (
                <span>
                    {ch.videoParams && <VideoPlayerButton text={valueDisplay} textColor="#00008B" showCameraIcon={false} {...ch.videoParams} />}
                    {!ch.videoParams && valueDisplay }
                </span>
            );
        },
    },
    {
        id: 'strikes.percentage',
        title: () => (<StatTooltip statistic="Stk">{translate('sections.opponent.player.strike-pct')}</StatTooltip>),
        type: NODE,
        cellClass: ({ strikes }) => strikes?.class,
        transform: ({ strikes, pf }) => {
            if (!strikes) return (<span />);
            if (pf < 10) { return <span>--</span>; }
            const valueDisplay = percent(strikes.percentage);
            return (
                <span>
                    {strikes.videoParams && <VideoPlayerButton text={valueDisplay} textColor="#00008B" showCameraIcon={false} {...strikes.videoParams} />}
                    {!strikes.videoParams && valueDisplay }
                </span>
            );
        },
    },
    {
        id: 'miss.percentage',
        title: () => (<StatTooltip statistic="Ms">{translate('sections.opponent.player.miss-pct')}</StatTooltip>),
        type: NODE,
        cellClass: ({ miss }) => miss?.class,
        transform: ({ miss, pf }) => {
            if (!miss) return (<span />);
            if (pf < 10) { return <span>--</span>; }
            const valueDisplay = percent(miss.percentage);
            return (
                <span>
                    {miss.videoParams && <VideoPlayerButton text={valueDisplay} textColor="#00008B" showCameraIcon={false} {...miss.videoParams} />}
                    {!miss.videoParams && valueDisplay }
                </span>
            );
        },
    },
    { id: 'hitvelo.velo', title: () => (<StatTooltip statistic="ExitVelo">{translate('sections.opponent.player.exit-velo')}</StatTooltip>), precision: 1, cellClass: ({ hitvelo }) => hitvelo?.class },
    { id: 'slg.decimal', title: () => (<StatTooltip statistic="SLG">{translate('baseball.abbr-slugging')}</StatTooltip>), type: RATE, cellClass: (pt) => pt.slg?.class },
    { id: 'gb.percentage', title: () => (<StatTooltip statistic="GBPercent">{translate('baseball.abbr-groundball-pct')}</StatTooltip>), type: PERCENT, cellClass: ({ gb }) => gb?.class },
    { id: 'grade', title: 'Overall Grade', precision: 0, cellClass: ({ grade }) => `background-grade${grade}` },
];

// This report allows a generic "vs RHB" and "vs LHB" which is signaled by the isGenericHandednessPlayer flag
const MatchupReport = ({ pitcher, pitcherId, batter, batterId, batterName, currentYear, isLHB, isLHP, isGenericHandednessPlayer }) => {
    const { t: translate } = useTranslation();
    const [useSpanish, setUseSpanish] = useState(false);
    const [onePlusNotesHaveSpanish, setOnePlusNotesHaveSpanish] = useState(false);
    const { state: { user } } = useContext(store);

    const opposingTeam = useOpposingTeamSelector();
    const userPlayer = usePlayerSelector();
    const [hasCoachNote, setHasCoachNote] = useState(true);
    const hasCoachNoteCallback = (data) => setHasCoachNote(data);
    // const milbLevels = [1, 2, 3, 4, 5, 10];
    const playerGenericPitcherR = { PlayerID: 1, FullName: translate('baseball.vs-rhp'), Throws: 1, Bats: 0, name: translate('baseball.vs-rhp'), value: 1, isGenericHandednessPlayer: true };
    const playerGenericPitcherL = { PlayerID: 2, FullName: translate('baseball.vs-lhp'), Throws: 2, Bats: 0, name: translate('baseball.vs-lhp'), value: 2, isGenericHandednessPlayer: true };

    const dataLevelId = opposingTeam.LevelIDData;// milbLevels.includes(pitcher?.Team?.LevelID) ? 7 : 8;
    const batterLevelId = batter?.Team?.LevelID || dataLevelId;

    const { data: repertoire, isFetching: isRepertoireLoading } = useOktaQuery(['repertoire', pitcherId], (t) => getPitcherRepertoire(t, pitcherId));

    const { data: heatmaps, isFetching: isOverviewLoading } = useOktaQuery(
        ['overview-heatmaps', batterId, pitcherId],
        (t) => getMatchupHeatmaps(translate, t, batterId, pitcherId, userPlayer.TeamID, opposingTeam.TeamID),
        { enabled: isGenericHandednessPlayer === false },
    );
    const { data: heatmapImages, isLoading: isHeatmapsLoading } = useOktaQuery(
        ['catcher-wristband', pitcherId, batterId, currentYear, userPlayer.TeamID, opposingTeam],
        (t) => HeatMaps.catcherWristband(t, pitcherId, batterId, currentYear, { nyyteamid: userPlayer.TeamID, vsteamid: opposingTeam.TeamID }),
        { enabled: isGenericHandednessPlayer === false },
    );

    const { data: report, isFetching: isReportLoading } = useOktaQuery(
        ['batter-splits', batterId, dataLevelId, currentYear, isLHP],
        // We need to specify a level for adov, not the regular 7/8, because that report doesn't support level 7
        // eslint-disable-next-line max-len
        (t) => getAdvanceOverview(translate, t, { playerId: batterId, levelId: batterLevelId, startYear: currentYear - PAST_YEARS_TO_USE, endYear: currentYear, isLHP }, isLHB, isLHP),
        { enabled: window.$MatchupEmbeddedVideoDisabled },
    );

    const { data: teamRoster, isFetching: isRosterLoading } = useOktaQuery(
        ['teamRoster', userPlayer.TeamID],
        (t) => Team.loginroster(t, userPlayer.TeamID),
    );

    const { data: bSeasonSplits, isFetching: isSeasonSplitsLoading } = useOktaQuery(
        ['bseason-splits', batterId],
        (t) => Stats.bSeasonSplits(t, { playerid: batterId, splitid: isLHP ? 2 : 1, yearid: currentYear, levelid: batterLevelId }),
    );

    useEffect(() => {
        setOnePlusNotesHaveSpanish(false);
        setUseSpanish(false);
    }, [pitcherId, batterId]);

    const isLoading = isRepertoireLoading || isOverviewLoading || isHeatmapsLoading || isReportLoading;
    const swingRates = repertoire && report && [
        ...repertoire.map(({ FXType: pitchType, FXSubType: subType }) => ({
            pitchType, subType, ...(report.swingRateData[pitchType] ? report.swingRateData[pitchType][subType] : []),
        })).filter((x) => x['0-0'] != null), // filter out lines with no pitch data
        { pitchType: null, subType: null, ...report.overallSwings },
    ];

    const swingRateGenericHandedness = (reportdata) => {
        const returnData = [];
        if (reportdata) {
            Object.keys(reportdata.swingRateData).forEach((ptKey) => {
                const pitchType = parseInt(ptKey);
                const subMap = reportdata.swingRateData[ptKey];
                Object.keys(subMap).forEach((stKey) => {
                    const subType = parseInt(stKey);
                    returnData.push({
                        pitchType,
                        subType,
                        ...subMap[stKey],
                    });
                });
            });
            returnData.push({ pitchType: null, subType: null, ...reportdata.overallSwings });
        }
        return [...returnData];
    };

    const pitchTypeResults = repertoire && report && heatmaps && [
        ...repertoire.map((r) => ({ pitchtype: r.FXType, fxsubtype: r.FXSubType ?? 0 })),
        ...(heatmaps.showFb94 ? [{ pitchtype: 1, fxsubtype: 0, pitchsubtype: 94 }] : []),
        { overall: true },
    ]
        .map((item) => {
            if (item.overall) return report.overallResults;
            const record = report.pitchTypeResults.find((ptr) => Object.values(ptr)[0]?.pitchtype === item.pitchtype);
            if (!record) return null;
            const bucket = record[item.fxsubtype];
            if (!bucket) return null;
            return bucket;
        })
        .filter((x) => x);
    const pitchTypeResultsGenericHandedness = report && [
        ...report.pitchTypeResults.flatMap((ptr) => Object.values(ptr)),
        report.overallResults,
    ];

    const heatMapSources = heatmapImages?.reduce((acc, i) => {
        if (!acc[i.PitchType]) acc[i.PitchType] = { };
        if (!acc[i.PitchType][i.FxSubType || 0]) acc[i.PitchType][i.FxSubType || 0] = {};
        acc[i.PitchType][i.FxSubType || 0][i.Split] = `data:${i.ContentType};base64,${i.HeatMapImg}`;
        return acc;
    }, {});

    const rosterCatchers = teamRoster?.filter((px) => (px.PrimaryPosition === 2 || px.Position === 2) && (px.RosterState === 1 || px.RosterState === 2))
        .sort((a, b) => (a.LastName < b.LastName ? -1 : 1));

    if (!window.$MatchupEmbeddedVideoDisabled) {
        mapVideoParamsToSwingRates(translate, isGenericHandednessPlayer ? swingRateGenericHandedness(report) : swingRates,
            batterId, isLHP);
        mapVideoParamsToPitchTypeResults(
            isGenericHandednessPlayer && pitchTypeResultsGenericHandedness ? pitchTypeResultsGenericHandedness : pitchTypeResults,
            batterId, isLHP,
        );
    }
    return (
        <StyledMatchupReport>
            <Row>
                <Col md={6}>
                    <BatterHeader isLhp={isLHP} batter={batter} split={!isSeasonSplitsLoading && bSeasonSplits ? bSeasonSplits[0] : {}} levelId={opposingTeam.LevelID} />
                </Col>
                <Col md={6}>
                    {onePlusNotesHaveSpanish === true && (
                        <div className="d-flex align-items-center justify-content-end mb-3">
                            <Checkbox
                                id="showspanish"
                                label={translate('sections.opponent.player.show-spanish-notes')}
                                checked={useSpanish}
                                onChange={(e) => setUseSpanish(e.currentTarget.checked)}
                            />
                        </div>
                    )}
                </Col>
            </Row>
            <Row>
                {!isGenericHandednessPlayer && (
                    <>
                        <Col md={6} sm={12}>
                            {userPlayer && batter && (
                                <PlayerAuthoredReportNote
                                    type="mynt"
                                    section="1"
                                    player={userPlayer}
                                    batter={batter}
                                    pitcher={pitcher.Throws === 2 ? playerGenericPitcherL : playerGenericPitcherR}
                                    customTitleForModal={batter.FullName}
                                    showSpanish={useSpanish}
                                    hasSpanishDataCallback={() => setOnePlusNotesHaveSpanish(true)} // only returns where there is spanish data
                                />
                            )}
                            {userPlayer && batter && pitcherId && userPlayer.PlayerID !== pitcherId && (
                                <PlayerAuthoredReportNote
                                    type="mynt"
                                    section="1"
                                    player={pitcher}
                                    batter={batter}
                                    showSpanish={useSpanish}
                                    hasSpanishDataCallback={() => setOnePlusNotesHaveSpanish(true)}
                                    hideWhenNotFound
                                />
                            )}
                        </Col>
                        <Col md={6} sm={12}>
                            <OpposingBatterMatchupNote
                                pitcherId={pitcherId}
                                batterId={batterId}
                                showSpanish={useSpanish}
                                doesHaveSpanishNoteCallback={() => setOnePlusNotesHaveSpanish(true)}
                            />
                        </Col>
                    </>
                )}
                {isGenericHandednessPlayer && (
                    <>
                        <Col md={6} sm={12}>
                            {/* Add the generic R/L player to match the pitcher's real handedness (for note saving reasons to playerid 1 or 2) */}
                            {userPlayer && batter && (
                                <PlayerAuthoredReportNote
                                    type="mynt"
                                    section="1"
                                    player={userPlayer}
                                    batter={batter}
                                    pitcher={isLHP ? playerGenericPitcherL : playerGenericPitcherR}
                                    customTitle={`${isLHP ? translate('baseball.lhp') : translate('baseball.rhp')} ${translate('sections.opponent.player.notes')}`}
                                    customTitleForModal={batter.FullName}
                                    showSpanish={useSpanish}
                                    hasSpanishDataCallback={() => setOnePlusNotesHaveSpanish(true)} // only returns where there is spanish data
                                />
                            )}
                        </Col>
                        <Col md={6} sm={12}>
                            <MatchupReportCoachNote
                                hasCoachNote={hasCoachNote}
                                isLHP={isLHP}
                                batterId={batterId}
                                hasCoachNoteCallback={hasCoachNoteCallback}
                                showSpanish={useSpanish}
                                doesHaveSpanishNoteCallback={() => setOnePlusNotesHaveSpanish(true)} // only returns where there is spanish data
                            />
                        </Col>
                    </>
                )}
            </Row>
            {userPlayer.IsPitcher && (
                <>
                    {isRosterLoading && <Row><SmallSpinner /></Row>}
                    {!isRosterLoading && rosterCatchers?.length > 0 && (
                        <Row>
                            {rosterCatchers?.map((catcher) => (
                                <Col key={catcher.PlayerID} md={6} sm={12}>
                                    <PlayerAuthoredReportNote
                                        type="mynt"
                                        section="1"
                                        player={catcher}
                                        batter={batter}
                                        pitcher={isLHP ? playerGenericPitcherL : playerGenericPitcherR}
                                        showSpanish={useSpanish}
                                        hasSpanishDataCallback={() => setOnePlusNotesHaveSpanish(true)} // only returns where there is spanish data
                                        hideWhenNotFound
                                    />
                                </Col>
                            ))}
                        </Row>
                    )}
                </>
            )}
            {!isGenericHandednessPlayer && repertoire && heatmaps && heatMapSources && [1, 2].indexOf(heatmaps?.heatmapUpdated?.hbpflag) >= 0 && Object.keys(heatMapSources).length > 0 && (
                <ReportCard>
                    <div className={`hbpflag${heatmaps?.heatmapUpdated?.hbpflag}`}>
                        {heatmaps?.heatmapUpdated?.hbpflag === 1 && (
                            <h4>{translate('sections.opponent.player.hbp-risk-moderate')}</h4>
                        )}
                        {heatmaps?.heatmapUpdated?.hbpflag === 2 && (
                            <h4>{translate('sections.opponent.player.hbp-risk-high')}</h4>
                        )}
                    </div>
                </ReportCard>
            )}
            {!isGenericHandednessPlayer && repertoire && heatmaps && heatMapSources && Object.keys(heatMapSources).length > 0 && (
                // eslint-disable-next-line max-len
                <ReportCard title={<>{translate('sections.opponent.player.attack-counts')} <small>(0-0, 1-0, 1-1, 0-1, 2-0, 2-1, 3-0, 3-1){heatmaps.heatmapUpdated && heatmaps.heatmapUpdated.venuename != null && (<span> (at {heatmaps.heatmapUpdated.venuename})</span>)}</small></>}>
                    <StyledPitchGroupBox $islhb={isLHB}>
                        {repertoire.map(({ FXType, FXSubType, name }) => (
                            <div key={FXType * 1000 + FXSubType}>
                                {heatMapSources[FXType] != null && heatMapSources[FXType][FXSubType] != null && (
                                    <>
                                        <p>{FXType ? pitchTypeDisplayName({ fxtype: FXType, fxsubtype: FXSubType }, true, true, translate) : name}</p>
                                        <CatcherWristbandHeatMap
                                            is2K={false}
                                            grade={heatmaps.earlyOverrides[FXType][FXSubType]?.val}
                                            src={heatMapSources[FXType][FXSubType]?.Early}
                                            redX={heatmaps.earlyOverrides[FXType][FXSubType]?.RedX}
                                            selected={heatmaps.earlyOverrides[FXType][FXSubType]?.Selected}
                                            kFree={heatmaps.earlyOverrides[FXType][FXSubType]?.KFree}
                                        />
                                    </>
                                )}
                            </div>
                        ))}
                    </StyledPitchGroupBox>
                </ReportCard>
            )}
            {!isGenericHandednessPlayer && repertoire && heatmaps && heatMapSources && Object.keys(heatMapSources).length > 0 && (
                // eslint-disable-next-line max-len
                <ReportCard title={<>2-{translate('baseball.strikes_one')} {translate('baseball.count')} <small>(0-2, 1-2, 2-2, 3-2){heatmaps.heatmapUpdated && heatmaps.heatmapUpdated.venuename != null && (<span> (at {heatmaps.heatmapUpdated.venuename})</span>)}</small></>}>
                    <StyledPitchGroupBox $islhb={isLHB}>
                        {repertoire.map(({ FXType, FXSubType, name }) => (
                            <div key={FXType * 1000 + FXSubType}>
                                {heatMapSources[FXType] != null && heatMapSources[FXType][FXSubType] != null && (
                                    <>
                                        <p>{FXType ? pitchTypeDisplayName({ fxtype: FXType, fxsubtype: FXSubType }, true, true, translate) : name}</p>
                                        <CatcherWristbandHeatMap
                                            is2K
                                            grade={heatmaps.lateOverrides[FXType][FXSubType]?.val}
                                            src={heatMapSources[FXType][FXSubType]?.TwoStrikes}
                                            redX={heatmaps.lateOverrides[FXType][FXSubType]?.RedX}
                                            selected={heatmaps.lateOverrides[FXType][FXSubType]?.Selected}
                                            otz={heatmaps.lateOverrides[FXType][FXSubType]?.OTZ}
                                        />
                                    </>
                                )}
                            </div>
                        ))}
                    </StyledPitchGroupBox>
                </ReportCard>
            )}
            {isGenericHandednessPlayer && <MatchupReportGenericHandednessHeatmaps pastYearsToUse={PAST_YEARS_TO_USE} batterId={batterId} isLHB={isLHB} isLHP={isLHP} />}
            <StyledReportCard>
                <div className="card-body-styled ">
                    <div className="card-body-border ">
                        <Row>
                            <Col md={6} sm={12}>
                                <ReportCard title={`${translate('sections.opponent.player.swing-rate-count')} ${currentYear - PAST_YEARS_TO_USE} - ${currentYear}`}>
                                    {!isGenericHandednessPlayer && !!swingRates?.length && (
                                        <DataTable columns={swingRateTableColumns(translate)} rows={swingRates} />
                                    )}
                                    {!isGenericHandednessPlayer && !(swingRates?.length) && <LoadingSpinner />}
                                    {isGenericHandednessPlayer && report && (
                                        <DataTable columns={swingRateTableColumns(translate)} rows={swingRateGenericHandedness(report)} />
                                    )}
                                    {isGenericHandednessPlayer && !report && <LoadingSpinner />}
                                </ReportCard>
                            </Col>
                            <Col md={6} sm={12}>
                                <ReportCard title={`${batterName} ${translate('baseball.abbr-versus')} ${isLHP ? translate('baseball.lhp') : translate('baseball.rhp')} ${currentYear - PAST_YEARS_TO_USE} - ${currentYear}`}>
                                    {!isGenericHandednessPlayer && !!pitchTypeResults?.length && (
                                        <DataTable columns={pitchTypeResultsColumns(translate, user)} rows={pitchTypeResults} />
                                    )}
                                    {!isGenericHandednessPlayer && !(pitchTypeResults?.length) && <LoadingSpinner />}
                                    {isGenericHandednessPlayer && pitchTypeResultsGenericHandedness && (
                                        <DataTable columns={pitchTypeResultsColumns(translate, user)} rows={pitchTypeResultsGenericHandedness} />
                                    )}
                                    {isGenericHandednessPlayer && !pitchTypeResultsGenericHandedness && <LoadingSpinner />}
                                </ReportCard>
                            </Col>
                        </Row>
                    </div>
                </div>
            </StyledReportCard>
            {!window.$MatchupHittingMonitorDisabled && (
                <MatchupReportHitMon
                    batterId={batterId}
                    batterName={batterName}
                    levelId={batterLevelId}
                    isLHB={isLHB}
                    isLHP={isLHP}
                    currentYear={currentYear}
                />
            )}
            {isLoading && <LoadingSpinner />}
            {!isGenericHandednessPlayer && (
                <MatchupReportCoachNote
                    hasCoachNote={hasCoachNote}
                    isLHP={isLHP}
                    batterId={batterId}
                    hasCoachNoteCallback={hasCoachNoteCallback}
                    showSpanish={useSpanish}
                    doesHaveSpanishNoteCallback={() => setOnePlusNotesHaveSpanish(true)} // only returns where there is spanish data
                />
            )}
        </StyledMatchupReport>
    );
};

MatchupReport.propTypes = {
    currentYear: PropTypes.number.isRequired,
    isLHP: PropTypes.bool.isRequired,
    pitcherId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isLHB: PropTypes.bool.isRequired,
    batterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    batterName: PropTypes.string.isRequired,
    batter: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
        Team: PropTypes.shape({
            LevelID: PropTypes.number,
        }),
    }).isRequired,
    pitcher: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
        Team: PropTypes.shape({
            LevelID: PropTypes.number,
        }),
    }).isRequired,
    isGenericHandednessPlayer: PropTypes.bool.isRequired,
};

export default MatchupReport;
